// Personel ve Roller — Yönetim › Personel (apiMode="clinic") ve admin klinik
// detayı (apiMode="admin") sekmesi.
//
// Tek veri akışı (personel listesi) → beyaz zemin, kutusuz (design-principles §1, §3):
// araç satırı (arama + "Rol şablonları" + "Onay bekleyenler" + "Personel Ekle") →
// rol filtre çipleri (sayılı) → koşullu bekleyen şeridi → çerçevesiz tablo.
// Satır tıklanınca personel drawer'ı açılır (düzenleme + silme orada).
// Rol şablonları ve onay bekleyenler ikincil listeler → sağdan drawer.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, ButtonBase, Typography, alpha } from "@mui/material";
import {
  AdminPanelSettings as AdminIcon,
  ChevronRight as ChevronRightIcon,
  HourglassEmpty as HourglassIcon,
  Shield as ShieldIcon,
} from "@mui/icons-material";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { getRoleLabel } from "../../../utils/roleLabels";
import {
  useListClinicStaffMutation,
  useListMyClinicStaffMutation,
  useCreateClinicStaffMutation,
  useCreateMyClinicStaffMutation,
  useUpdateClinicStaffMutation,
  useUpdateMyClinicStaffMutation,
} from "../../../store/clinicApi";
import {
  useDeleteUserMutation,
  useDeleteClinicUserMutation,
} from "../../../store/userAccountApi";
import {
  useLazyListPendingUsersQuery,
  useApprovePendingUserMutation,
  useRejectPendingUserMutation,
  useLazyListClinicPendingUsersQuery,
  useApproveClinicPendingUserMutation,
  useRejectClinicPendingUserMutation,
} from "../../../store/pendingUserApi";
import {
  useListRoleTemplatesQuery, useCreateRoleTemplateMutation,
  useUpdateRoleTemplateMutation, useDeleteRoleTemplateMutation,
  useGetAvailablePermissionsQuery,
} from "../../../store/roleTemplateApi";
import { useAlert } from "../../../context/AlertProvider";
import useAuth from "../../../hooks/useAuth";
import { useHasPermission } from "../../../hooks/useHasPermission";
import {
  ActionButton, ChoiceChipGroup, SearchField, formatTRPhone,
} from "../../../components/atoms";
import EmptyState from "../../../components/atoms/EmptyState";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import StaffTable from "./staff/StaffTable";
import StaffDrawer from "./staff/StaffDrawer";
import RolesDrawer from "./staff/RolesDrawer";
import RoleEditorDrawer from "./staff/RoleEditorDrawer";
import PendingStaffDrawer from "./staff/PendingStaffDrawer";
import { groupPermissions, roleNameOf as roleNameOfBase, stripDigits } from "./staff/staffFormat";

const collator = new Intl.Collator("tr", { sensitivity: "base" });
const fullNameOf = (u) => `${u?.name || ""} ${u?.surname || ""}`.trim();
const DEFAULT_DIR = { name: "asc", role: "asc", status: "asc" };
const EMPTY_CREATE_FORM = { name: "", surname: "", email: "", gender: "", tellNo: "", tcNo: "", roleTemplateId: "" };

/** Araç satırındaki sayaçlı ikincil buton (Hastalar › "Bekleyen talepler" ile aynı dil). */
function CountButton({ icon, label, count, highlight, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        height: 36,
        borderRadius: 2,
        fontSize: TYPO.body,
        fontWeight: 600,
        border: `1px solid ${highlight ? alpha(COLORS.warning, 0.4) : COLORS.slate200}`,
        bgcolor: highlight ? COLORS.warningLightest : COLORS.white,
        color: highlight ? COLORS.warningDarker : COLORS.slate600,
        "&:hover": { bgcolor: highlight ? COLORS.warningLight : COLORS.slate50 },
      }}
    >
      {icon}
      {label}
      <Box
        component="span"
        sx={{
          minWidth: 20,
          height: 20,
          px: 0.5,
          borderRadius: 10,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: TYPO.caption,
          bgcolor: highlight ? COLORS.warning : COLORS.slate100,
          color: highlight ? COLORS.white : COLORS.slate500,
        }}
      >
        {count}
      </Box>
    </ButtonBase>
  );
}

export default function StaffTab({ clinic, clinicId, token, palette, apiMode = "admin" }) {
  const isClinicMode = apiMode === "clinic";
  const { showSuccess, showError } = useAlert();
  const { auth } = useAuth();
  const isAdminRole = auth?.role === "Admin";
  const clinicManagerId = clinic?.clinicManager;

  /* ── Staff ── */
  const [listClinicStaffAdmin, { isLoading: isAdminStaffLoading }] = useListClinicStaffMutation();
  const [listClinicStaffMine, { isLoading: isMineStaffLoading }] = useListMyClinicStaffMutation();
  const listClinicStaff = isClinicMode ? listClinicStaffMine : listClinicStaffAdmin;
  const isStaffLoading = isClinicMode ? isMineStaffLoading : isAdminStaffLoading;
  const [deleteUserAdmin, { isLoading: isAdminDeleting }] = useDeleteUserMutation();
  const [deleteUserClinic, { isLoading: isClinicDeleting }] = useDeleteClinicUserMutation();
  const deleteUser = isClinicMode ? deleteUserClinic : deleteUserAdmin;
  const isDeleting = isClinicMode ? isClinicDeleting : isAdminDeleting;
  const [createStaffAdmin, { isLoading: isCreatingAdmin }] = useCreateClinicStaffMutation();
  const [createStaffMine, { isLoading: isCreatingMine }] = useCreateMyClinicStaffMutation();
  const createStaff = isClinicMode ? createStaffMine : createStaffAdmin;
  const isCreatingStaff = isClinicMode ? isCreatingMine : isCreatingAdmin;
  const [updateStaffAdmin, { isLoading: isUpdatingAdmin }] = useUpdateClinicStaffMutation();
  const [updateStaffMine, { isLoading: isUpdatingMine }] = useUpdateMyClinicStaffMutation();
  const updateStaff = isClinicMode ? updateStaffMine : updateStaffAdmin;
  const isUpdatingStaff = isClinicMode ? isUpdatingMine : isUpdatingAdmin;
  const [staffList, setStaffList] = useState([]);
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [staffDrawerOpen, setStaffDrawerOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({ roleTemplateId: "", name: "", surname: "", gender: "", tellNo: "" });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, user: null });
  const [createStaffDrawerOpen, setCreateStaffDrawerOpen] = useState(false);
  const [createStaffForm, setCreateStaffForm] = useState(EMPTY_CREATE_FORM);

  /* ── List state (istemci tarafı: arama + rol filtresi + sıralama + sayfalama) ── */
  const [search, setSearchRaw] = useState("");
  const [roleFilter, setRoleFilterRaw] = useState("all");
  const [sort, setSort] = useState("name");
  const [dir, setDir] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const setSearch = (v) => { setSearchRaw(v); setPage(0); };
  const setRoleFilter = (v) => { setRoleFilterRaw(v); setPage(0); };

  /* ── Roles ── */
  const { data: templatesData, isLoading: isTemplatesLoading, isFetching: isTemplatesFetching } = useListRoleTemplatesQuery({ token, clinicId }, { skip: !token || !clinicId, refetchOnMountOrArgChange: true });
  const rawTemplates = templatesData?.data?.templates || templatesData?.templates || templatesData?.data;
  const roleTemplates = useMemo(() => (Array.isArray(rawTemplates) ? rawTemplates : []), [rawTemplates]);
  const { data: permissionsData } = useGetAvailablePermissionsQuery({ token, clinicId }, { skip: !token });
  const rawPerms = permissionsData?.data?.permissions || permissionsData?.permissions;
  const availablePermissions = useMemo(() => (Array.isArray(rawPerms) ? rawPerms : []), [rawPerms]);
  const groupedPermissions = useMemo(() => groupPermissions(availablePermissions), [availablePermissions]);
  const [createTemplate, { isLoading: isRoleCreating }] = useCreateRoleTemplateMutation();
  const [updateTemplate, { isLoading: isRoleUpdating }] = useUpdateRoleTemplateMutation();
  const [deleteTemplate, { isLoading: isRoleDeleting }] = useDeleteRoleTemplateMutation();
  const [rolesDrawerOpen, setRolesDrawerOpen] = useState(false);
  const [roleDrawerOpen, setRoleDrawerOpen] = useState(false);
  const [roleDrawerMode, setRoleDrawerMode] = useState("create");
  const [editingRole, setEditingRole] = useState(null);
  const [formName, setFormName] = useState("");
  const [formPermissions, setFormPermissions] = useState([]);
  const [roleDeleteDialog, setRoleDeleteDialog] = useState({ open: false, template: null });

  /* ── Pending Users ── */
  const [pendingDrawerOpen, setPendingDrawerOpen] = useState(false);
  const [fetchPendingUsersAdmin, { data: pendingDataAdmin, isLoading: isPendingLoadingAdmin, isFetching: isPendingFetchingAdmin, isUninitialized: isPendingUninitializedAdmin }] = useLazyListPendingUsersQuery();
  const [fetchPendingUsersClinic, { data: pendingDataClinic, isLoading: isPendingLoadingClinic, isFetching: isPendingFetchingClinic, isUninitialized: isPendingUninitializedClinic }] = useLazyListClinicPendingUsersQuery();
  const fetchPendingUsers = isClinicMode ? fetchPendingUsersClinic : fetchPendingUsersAdmin;
  const pendingData = isClinicMode ? pendingDataClinic : pendingDataAdmin;
  const isPendingLoading = isClinicMode ? isPendingLoadingClinic : isPendingLoadingAdmin;
  const isPendingFetching = isClinicMode ? isPendingFetchingClinic : isPendingFetchingAdmin;
  const isPendingUninitialized = isClinicMode ? isPendingUninitializedClinic : isPendingUninitializedAdmin;
  const [approvePendingAdmin, { isLoading: isApprovingAdmin }] = useApprovePendingUserMutation();
  const [approvePendingClinic, { isLoading: isApprovingClinic }] = useApproveClinicPendingUserMutation();
  const approvePending = isClinicMode ? approvePendingClinic : approvePendingAdmin;
  const isApproving = isClinicMode ? isApprovingClinic : isApprovingAdmin;
  const [rejectPendingAdmin, { isLoading: isRejectingAdmin }] = useRejectPendingUserMutation();
  const [rejectPendingClinic, { isLoading: isRejectingClinic }] = useRejectClinicPendingUserMutation();
  const rejectPending = isClinicMode ? rejectPendingClinic : rejectPendingAdmin;
  const isRejecting = isClinicMode ? isRejectingClinic : isRejectingAdmin;
  const rawPending =
    pendingData?.data?.pendingUsers ||
    pendingData?.data?.users ||
    pendingData?.pendingUsers ||
    pendingData?.users ||
    pendingData?.data;
  const allPending = Array.isArray(rawPending) ? rawPending : [];
  // Klinik bazlı filtrele (server tüm pending'leri döndürebilir). Klinik
  // tarafında server zaten kendi klinik scope'unda filtreler; admin tarafında
  // ek filtre güvence olur. Populate sonrası u.clinic obje olabilir.
  const targetClinicId = String(clinicId || "");
  const pendingUsers = allPending.filter((u) => {
    if (!targetClinicId) return true;
    const c = u?.clinic;
    if (!c) return false;
    const cid = typeof c === "object" ? String(c._id || c.id || c) : String(c);
    return cid === targetClinicId || u.clinicId === clinicId;
  });
  const isPendingBusy = isPendingUninitialized || isPendingLoading || isPendingFetching;

  /* ── Fetch staff ── */
  const fetchStaff = useCallback(async () => {
    if (!token || !clinicId) return;
    try {
      const res = await listClinicStaff({ token, clinicId }).unwrap();
      const raw = res?.data;
      if (Array.isArray(raw)) setStaffList(raw);
      else if (raw?.staff) setStaffList(Object.values(raw.staff).flat());
      else setStaffList([]);
    } catch (_e) { /* handled silently */ }
    finally { setStaffLoaded(true); }
  }, [token, clinicId, listClinicStaff]);
  useEffect(() => { fetchStaff(); }, [fetchStaff]);
  useEffect(() => { if (token) fetchPendingUsers({ token }); }, [token, fetchPendingUsers]);

  // İzin anahtarları admin/klinik bağlamına göre değişir; klinik tarafında
  // ClinicStaff users:read / users:create / users:update / users:delete /
  // clinic:roleTemplates kullanır. Admin tarafında personel uçları
  // (list/create/update) sunucuda sadece restrictTo("Admin") ile korunuyor —
  // sunucuda karşılığı olmayan "admin-clinics:*-staff" anahtarları yerine
  // doğrudan rol kontrolü (isAdminRole) kullanılır.
  const PERM = isClinicMode
    ? {
        viewRoles: "clinic:roleTemplates",
        createRole: "clinic:roleTemplates",
        updateRole: "clinic:roleTemplates",
        deleteRole: "clinic:roleTemplates",
      }
    : {
        viewRoles: "admin-clinics:view-roles",
        createRole: "admin-clinics:create-role",
        updateRole: "admin-clinics:update-role",
        deleteRole: "admin-clinics:delete-role",
      };
  const clinicCanReadStaff = useHasPermission("users:read");
  const clinicCanCreateStaff = useHasPermission("users:create");
  const clinicCanUpdateStaff = useHasPermission("users:update");
  const clinicCanDeleteStaff = useHasPermission("users:delete");
  const canViewStaff = isClinicMode ? clinicCanReadStaff : isAdminRole;
  const canCreateStaff = isClinicMode ? clinicCanCreateStaff : isAdminRole;
  const canEditStaff = isClinicMode ? clinicCanUpdateStaff : isAdminRole;
  const canDeleteStaff = isClinicMode ? clinicCanDeleteStaff : isAdminRole;
  const canViewRoles = useHasPermission(PERM.viewRoles);
  const canCreateRole = useHasPermission(PERM.createRole);
  const canUpdateRole = useHasPermission(PERM.updateRole);
  const canDeleteRole = useHasPermission(PERM.deleteRole);

  /* ── List derivation ── */
  const roleNameOf = useCallback((u) => roleNameOfBase(u, getRoleLabel(u?.role)), []);
  const searchText = search.trim();
  const matchesSearch = useCallback((u) => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.surname || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) || (u.tellNo || "").includes(q);
  }, [searchText]);
  const searched = useMemo(() => staffList.filter(matchesSearch), [staffList, matchesSearch]);
  const roleCounts = useMemo(() => {
    const c = { all: searched.length };
    searched.forEach((u) => {
      const k = String(u.roleTemplate?.templateId || "");
      if (k) c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [searched]);
  const staffCountByRole = useMemo(() => {
    const c = {};
    staffList.forEach((u) => {
      const k = String(u.roleTemplate?.templateId || "");
      if (k) c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [staffList]);
  const sortedRows = useMemo(() => {
    const list = roleFilter === "all"
      ? searched
      : searched.filter((u) => String(u.roleTemplate?.templateId || "") === String(roleFilter));
    const keyOf = {
      name: fullNameOf,
      role: roleNameOf,
      status: (u) => u.accountStatus || "",
    }[sort] || fullNameOf;
    const sign = dir === "asc" ? 1 : -1;
    const sorted = [...list].sort((a, b) => sign * collator.compare(keyOf(a), keyOf(b)));
    // Klinik yöneticisi her sıralamada en üstte kalır
    const mi = clinicManagerId ? sorted.findIndex((u) => u._id === clinicManagerId) : -1;
    if (mi > 0) sorted.unshift(sorted.splice(mi, 1)[0]);
    return sorted;
  }, [searched, roleFilter, sort, dir, roleNameOf, clinicManagerId]);
  const pagedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );
  const handleSort = (field) => {
    if (field === sort) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(field);
      setDir(DEFAULT_DIR[field] || "asc");
    }
    setPage(0);
  };
  // Silinen bir şablon seçili filtre olarak kalmasın
  useEffect(() => {
    if (roleFilter !== "all" && !roleTemplates.some((t) => String(t._id) === String(roleFilter))) {
      setRoleFilterRaw("all");
    }
  }, [roleTemplates, roleFilter]);

  /* ── Edit staff ── */
  const isEditFormValid = useMemo(() => {
    const f = staffForm;
    return Boolean(f.name?.trim() && f.surname?.trim() && f.gender && f.tellNo?.trim());
  }, [staffForm]);

  const openEditStaff = (u) => {
    setStaffForm({
      roleTemplateId: u.roleTemplate?.templateId || "",
      name: stripDigits(u.name || ""),
      surname: stripDigits(u.surname || ""),
      gender: u.gender || "",
      tellNo: formatTRPhone(u.tellNo || ""),
    });
    setEditingStaff(u);
    setStaffDrawerOpen(true);
  };
  const closeStaffDrawer = () => { setStaffDrawerOpen(false); };
  const setField = (key, value) => setStaffForm((p) => ({ ...p, [key]: value }));

  const handleEditStaffSubmit = async () => {
    if (!editingStaff || !isEditFormValid) return;
    try {
      await updateStaff({
        token,
        staffId: editingStaff._id,
        name: staffForm.name.trim(),
        surname: staffForm.surname.trim(),
        gender: staffForm.gender,
        tellNo: staffForm.tellNo,
        roleTemplateId: staffForm.roleTemplateId || undefined,
      }).unwrap();
      showSuccess("Personel güncellendi.");
      closeStaffDrawer();
      await fetchStaff();
    } catch (err) { showError(err?.data?.message || "Personel güncellenemedi."); }
  };

  const handleDeleteStaff = async () => {
    const u = deleteConfirm.user;
    if (!u) return;
    try {
      await deleteUser({ token, id: u._id }).unwrap();
      showSuccess(`${u.name} ${u.surname} silindi.`);
      setDeleteConfirm({ open: false, user: null });
      closeStaffDrawer();
      await fetchStaff();
    } catch (err) { showError(err?.data?.message || "Silme başarısız."); }
  };

  /* ── Create staff ── */
  const isCreateStaffValid = useMemo(() => {
    const f = createStaffForm;
    if (!f.name?.trim() || !f.surname?.trim()) return false;
    if (!f.email?.trim() || !f.gender?.trim() || !f.tellNo?.trim() || !f.tcNo?.trim()) return false;
    if (f.tcNo?.trim().length !== 11) return false;
    return true;
  }, [createStaffForm]);
  const openCreateStaff = () => { setCreateStaffForm(EMPTY_CREATE_FORM); setCreateStaffDrawerOpen(true); };
  const closeCreateStaffDrawer = () => { setCreateStaffDrawerOpen(false); };
  const setCreateField = (key, value) => setCreateStaffForm((p) => ({ ...p, [key]: value }));

  const handleCreateStaffSubmit = async () => {
    if (!token || !isCreateStaffValid) return;
    try {
      const payload = {
        token,
        role: "ClinicStaff",
        name: createStaffForm.name.trim(),
        surname: createStaffForm.surname.trim(),
        email: createStaffForm.email.trim(),
        gender: createStaffForm.gender,
        tellNo: createStaffForm.tellNo,
        tcNo: createStaffForm.tcNo.trim(),
        roleTemplateId: createStaffForm.roleTemplateId || undefined,
      };
      // Klinik modunda clinicId gönderilmez — sunucu req.user.clinic'ten alır.
      if (!isClinicMode) payload.clinicId = clinicId;
      await createStaff(payload).unwrap();
      showSuccess("Personel oluşturuldu.");
      closeCreateStaffDrawer();
      await fetchStaff();
    } catch (err) { showError(err?.data?.message || "Personel oluşturulamadı."); }
  };

  /* ── Pending ── */
  const handleApprovePending = async (u) => {
    try { await approvePending({ token, id: u._id }).unwrap(); showSuccess("Kullanıcı onaylandı"); fetchPendingUsers({ token }); fetchStaff(); }
    catch (e) { showError(e?.data?.message || "Onaylama başarısız"); }
  };
  const handleRejectPending = async (u) => {
    try { await rejectPending({ token, id: u._id, rejectionReason: "Reddedildi" }).unwrap(); showSuccess("Kullanıcı reddedildi"); fetchPendingUsers({ token }); }
    catch (e) { showError(e?.data?.message || "Reddetme başarısız"); }
  };

  /* ── Role helpers ── */
  const openRoleCreate = () => { setRoleDrawerMode("create"); setEditingRole(null); setFormName(""); setFormPermissions([]); setRoleDrawerOpen(true); };
  const openRoleEdit = (t) => { setRoleDrawerMode("edit"); setEditingRole(t); setFormName(t.name || ""); setFormPermissions(t.permissions || []); setRoleDrawerOpen(true); };
  const closeRoleDrawer = () => { setRoleDrawerOpen(false); setFormName(""); setFormPermissions([]); };
  const togglePermission = (p) => setFormPermissions((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  const toggleDomain = (domain, perms) => {
    const keys = perms.map((p) => p.key);
    const all = keys.every((k) => formPermissions.includes(k));
    if (all) setFormPermissions((prev) => prev.filter((p) => !keys.includes(p)));
    else setFormPermissions((prev) => [...new Set([...prev, ...keys])]);
  };
  const handleRoleSave = async () => {
    try {
      if (roleDrawerMode === "create") {
        const res = await createTemplate({ token, clinicId, name: formName.trim(), permissions: formPermissions }).unwrap();
        if (res?.status === "success" || res?.success) { showSuccess("Rol şablonu oluşturuldu."); closeRoleDrawer(); } else showError(res?.message || "Başarısız.");
      } else {
        const res = await updateTemplate({ token, clinicId, templateId: editingRole._id, name: formName.trim(), permissions: formPermissions }).unwrap();
        if (res?.status === "success" || res?.success) { showSuccess("Rol şablonu güncellendi."); closeRoleDrawer(); } else showError(res?.message || "Başarısız.");
      }
    } catch (err) { showError(err?.data?.message || "Hata oluştu."); }
  };
  const handleRoleDelete = async () => {
    if (!roleDeleteDialog.template) return;
    try {
      const res = await deleteTemplate({ token, clinicId, templateId: roleDeleteDialog.template._id }).unwrap();
      if (res?.status === "success" || res?.success) {
        showSuccess("Şablon silindi.");
        setRoleDeleteDialog({ open: false, template: null });
        closeRoleDrawer();
      } else showError(res?.message || "Başarısız.");
    } catch (err) { showError(err?.data?.message || "Hata oluştu."); }
  };

  /* ── Copy helper ── */
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => showSuccess("Kod kopyalandı.")).catch(() => {});
  };

  /* ── Empty state (satır içi aksiyon bağlantısıyla) ── */
  const addLink = (label) =>
    canCreateStaff ? (
      <ButtonBase
        onClick={openCreateStaff}
        sx={{
          font: "inherit",
          color: palette.primary,
          fontWeight: 600,
          textDecoration: "underline",
          textUnderlineOffset: 3,
          verticalAlign: "baseline",
        }}
      >
        {label}
      </ButtonBase>
    ) : null;

  const empty = (() => {
    if (searchText) {
      return {
        title: "Personel bulunamadı",
        message: (
          <>
            “{searchText}” aramanızla eşleşen personel yok. Aramayı değiştirin
            {canCreateStaff ? <> veya {addLink("yeni personel ekleyin")}</> : null}.
          </>
        ),
      };
    }
    if (roleFilter !== "all") {
      return { title: "Bu rolde personel yok", message: "Seçili rol şablonuna atanmış personel bulunmuyor." };
    }
    return {
      title: "Henüz personel yok",
      message: canCreateStaff
        ? <>İlk personeli {addLink("ekleyerek")} başlayın.</>
        : "Klinikte kayıtlı personel bulunmuyor.",
    };
  })();

  const roleChipOptions = [
    { value: "all", label: `Tümü · ${roleCounts.all || 0}` },
    ...roleTemplates.map((t) => ({ value: String(t._id), label: `${t.name} · ${roleCounts[String(t._id)] || 0}` })),
  ];
  const pendingCount = pendingUsers.length;
  const editingIsManager = Boolean(editingStaff) && editingStaff._id === clinicManagerId;

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", bgcolor: COLORS.white }}>
      {/* Araç satırı */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          pt: 2.5,
          pb: canViewStaff ? 0 : 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        {canViewStaff ? (
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Ad, soyad, e-posta veya telefon ara"
            ariaLabel="Personel ara"
            autoComplete="off"
            sx={{ width: { xs: "100%", sm: 340 } }}
          />
        ) : null}
        <Box sx={{ flex: 1 }} />
        {canViewRoles ? (
          <CountButton
            icon={<ShieldIcon sx={{ fontSize: 16 }} />}
            label="Rol şablonları"
            count={roleTemplates.length}
            onClick={() => setRolesDrawerOpen(true)}
          />
        ) : null}
        <CountButton
          icon={<HourglassIcon sx={{ fontSize: 16 }} />}
          label="Onay bekleyenler"
          count={pendingCount}
          highlight={pendingCount > 0}
          onClick={() => setPendingDrawerOpen(true)}
        />
        {canCreateStaff ? (
          <ActionButton variant="add" palette={palette} size="medium" label="Personel Ekle" onClick={openCreateStaff} />
        ) : null}
      </Box>

      {canViewStaff ? (
        <>
          {/* Hazır filtreler: rol şablonları (sayılı) */}
          {roleTemplates.length > 0 ? (
            <Box sx={{ px: { xs: 2, md: 4 }, pt: 2, flexShrink: 0 }}>
              <ChoiceChipGroup
                ariaLabel="Role göre filtrele"
                options={roleChipOptions}
                value={roleFilter}
                onChange={setRoleFilter}
                palette={palette}
              />
            </Box>
          ) : null}
          <Box sx={{ height: 16, flexShrink: 0 }} />

          {/* Onay bekleyen şeridi — yalnızca bekleyen varken */}
          {pendingCount > 0 ? (
            <ButtonBase
              onClick={() => setPendingDrawerOpen(true)}
              sx={{
                mx: { xs: 2, md: 4 },
                mb: 2,
                px: 2,
                py: 1,
                borderRadius: 2,
                justifyContent: "flex-start",
                gap: 1,
                bgcolor: COLORS.warningLightest,
                border: `1px solid ${alpha(COLORS.warning, 0.3)}`,
                flexShrink: 0,
              }}
            >
              <HourglassIcon sx={{ fontSize: 16, color: COLORS.warningDark }} />
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.warningDarker, flex: 1, textAlign: "left" }}>
                <Box component="span" sx={{ fontWeight: 600 }}>{pendingCount} kullanıcı</Box> kliniğinize katılmak için onayınızı bekliyor.
              </Typography>
              <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.warningDarker }}>Görüntüle</Typography>
              <ChevronRightIcon sx={{ fontSize: 18, color: COLORS.warningDarker }} />
            </ButtonBase>
          ) : null}

          {/* Tablo — kalan alanı doldurur, kendi içinde kayar */}
          <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
            <StaffTable
              rows={pagedRows}
              totalCount={sortedRows.length}
              isLoading={isStaffLoading || !staffLoaded}
              managerId={clinicManagerId}
              roleNameOf={roleNameOf}
              palette={palette}
              sort={sort}
              dir={dir}
              onSort={handleSort}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              onOpen={openEditStaff}
              empty={empty}
            />
          </Box>
        </>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", borderTop: `1px solid ${COLORS.slate200}` }}>
          <EmptyState
            variant="illustrated"
            icon={<AdminIcon />}
            title="Personel listesi görüntülenemiyor"
            description="Bu bölümü görüntüleme yetkiniz yok. Gerekirse klinik yöneticinizden yetki isteyin."
          />
        </Box>
      )}

      {/* ── Drawer'lar ve onay pencereleri ── */}
      <StaffDrawer
        open={staffDrawerOpen}
        mode="edit"
        user={editingStaff}
        isManager={editingIsManager}
        form={staffForm}
        setField={setField}
        roleTemplates={roleTemplates}
        roleName={editingStaff ? roleNameOf(editingStaff) : ""}
        canEdit={canEditStaff}
        canDelete={canDeleteStaff && !editingIsManager}
        onDelete={() => setDeleteConfirm({ open: true, user: editingStaff })}
        onClose={closeStaffDrawer}
        onSubmit={handleEditStaffSubmit}
        loading={isUpdatingStaff}
        valid={isEditFormValid}
        palette={palette}
      />

      <StaffDrawer
        open={createStaffDrawerOpen}
        mode="create"
        form={createStaffForm}
        setField={setCreateField}
        roleTemplates={roleTemplates}
        onClose={closeCreateStaffDrawer}
        onSubmit={handleCreateStaffSubmit}
        loading={isCreatingStaff}
        valid={isCreateStaffValid}
        palette={palette}
      />

      <RolesDrawer
        open={rolesDrawerOpen}
        onClose={() => setRolesDrawerOpen(false)}
        templates={roleTemplates}
        isLoading={isTemplatesLoading || isTemplatesFetching}
        staffCountOf={(id) => staffCountByRole[String(id)] || 0}
        canCreate={canCreateRole}
        canOpen={canUpdateRole || canDeleteRole}
        onCreate={openRoleCreate}
        onOpen={openRoleEdit}
        onCopyCode={copyToClipboard}
        palette={palette}
      />

      <RoleEditorDrawer
        open={roleDrawerOpen}
        mode={roleDrawerMode}
        role={editingRole}
        name={formName}
        onNameChange={setFormName}
        permissions={formPermissions}
        groupedPermissions={groupedPermissions}
        onTogglePermission={togglePermission}
        onToggleDomain={toggleDomain}
        canEdit={canUpdateRole}
        canDelete={canDeleteRole}
        onDelete={() => setRoleDeleteDialog({ open: true, template: editingRole })}
        onCopyCode={copyToClipboard}
        onClose={closeRoleDrawer}
        onSubmit={handleRoleSave}
        loading={isRoleCreating || isRoleUpdating}
        palette={palette}
      />

      <PendingStaffDrawer
        open={pendingDrawerOpen}
        onClose={() => setPendingDrawerOpen(false)}
        users={pendingUsers}
        isLoading={isPendingBusy}
        onApprove={handleApprovePending}
        onReject={handleRejectPending}
        approving={isApproving}
        rejecting={isRejecting}
        palette={palette}
      />

      {/* Delete Staff Confirm */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, user: null })}
        onConfirm={handleDeleteStaff}
        title="Personeli Sil"
        message={`“${deleteConfirm.user?.name || ""} ${deleteConfirm.user?.surname || ""}” adlı personeli silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Evet, Sil"
        cancelText="Vazgeç"
        confirmVariant="error"
        loading={isDeleting}
        loadingText="Siliniyor..."
      />

      {/* Delete Role Confirm */}
      <ConfirmDialog
        open={roleDeleteDialog.open}
        onClose={() => setRoleDeleteDialog({ open: false, template: null })}
        onConfirm={handleRoleDelete}
        title="Şablonu Sil"
        message={`“${roleDeleteDialog.template?.name || ""}” şablonunu silmek istediğinizden emin misiniz?`}
        confirmText="Sil"
        cancelText="Vazgeç"
        confirmVariant="error"
        loading={isRoleDeleting}
        loadingText="Siliniyor..."
      />
    </Box>
  );
}
